import { Autocomplete, Button, Input } from "src/components";
import crossIcon from "src/assets/cross.svg";
import { useToast } from "src/hooks/useToast";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/context/store";
import {
  addCounterStockist,
  getCounterOrderProductsByItemCode,
  getDistrictStockist,
  getOrderDetailsByOrderCode,
  replaceStockist,
} from "src/services/order-api";
import { generateUniqueID } from "src/constants/functions";
import { useEventStatus } from "src/hooks/useEventStatus";
import { ordActions } from "src/context/order-slice";

const EditStockistName = ({ onCloseModal }: { onCloseModal: () => void }) => {
  const [remarks, setRemarks] = useState<string>("");
  const [makeDefaultStockist, setMakeDefaultStockist] = useState<boolean>(true);
  const { toast } = useToast();
  const { orderDetailsFiltered, selectedCounterData } = useSelector((state: RootState) => state.ord);
  const [districtStockist, setDistrictStockist] = useState<any[]>([]);
  const [selectedStockist, setSelectedStockist] = useState<any>(null);
  const { withEventCheck } = useEventStatus();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [clusterCode, setClusterCode] = useState<string>("");
  const dispatch = useDispatch();

  const handleSaveChanges = async () => {
    if (!selectedStockist) {
      toast({ description: "Please select stockist", variant: "destructive" });
      return;
    }
    if (!remarks) {
      toast({ description: "Please enter remarks", variant: "destructive" });
      return;
    }
    const orderDetails = orderDetailsFiltered?.[0];
    const payload = {
      data: {
        order_id: orderDetails?.order_id_int,
        old_company_stockist_code: orderDetails?.stockist?.company_stockist_code,
        old_stockist_name: orderDetails?.stockist?.firm_name,
        new_company_stockist_code: selectedStockist?.company_stockist_code,
        new_stockist_name: selectedStockist?.owner_name,
        reason: remarks,
      },
      primary_key: orderDetails?.order_id_int,
      event_type: "REPLACE_ORDER_STOCKIST",
    };
    try {
      setIsLoading(true);
      const response = await replaceStockist(payload);
      const response2 = await withEventCheck(
        () => {
          return getOrderDetailsByOrderCode(orderDetails?.order_code);
        },
        response?.data?.data?.eventStreamCreated?.streamId
      );
      dispatch(ordActions.setOrderDetailsFiltered([response2?.data?.data]));
      if (response?.status !== 200) throw new Error(response?.data?.message);
      const payload2 = {
        data: {
          is_default: true,
          counter_hdr_id: selectedCounterData?.counter_location?.counter_hdr_id?.counter_id,
          counter_company_id: selectedCounterData?.counter_company_location_id,
          remarks: remarks,
          location_id: selectedCounterData?.counter_location?.counter_location_id,
          cluster_code: selectedStockist?.cluster_code,
          company_code: selectedCounterData?.company_code,
          counter_code: selectedCounterData?.counter_location?.counter_hdr_id?.counter_code,
          stockist_cluster: selectedStockist?.stockist_cluster,
          u_code: generateUniqueID(),
        },
        primary_key: selectedCounterData?.counter_location?.counter_hdr_id?.counter_id,
        event_type: "COUNTER_STOCKIST_ADD",
      };
      toast({ description: "Stockist updated successfully", variant: "default" });
      if (makeDefaultStockist) {
        try {
          const res = await addCounterStockist(payload2);
          if (res?.status !== 200) throw new Error(res?.data?.message);
          toast({ description: "Stockist set as default successfully", variant: "default" });
        } catch (error: any) {
          toast({ description: error?.response?.data?.message, variant: "destructive" });
        }
      }
      onCloseModal();
    } catch (error: any) {
      toast({ description: error?.response?.data?.message, variant: "destructive" });
      onCloseModal();
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProductsData = async () => {
    try {
      setIsLoading(true);
      const res = await getCounterOrderProductsByItemCode(
        orderDetailsFiltered?.[0]?.counter_company_location_id,
        orderDetailsFiltered?.[0]?.orderProducts?.[0]?.item_code
      );
      setClusterCode(res?.data?.data?.[0]?.companyProducts?.item_division?.[0]?.cluster_code?.cluster_code);
      setIsLoading(false);
    } catch (error: any) {
      toast({ description: error?.response?.data?.message, variant: "destructive" });
      setIsLoading(false);
    }
  };

  const fetchDistrictStockistData = async () => {
    const response = await getDistrictStockist(selectedCounterData?.counter_company_location_id, clusterCode);
    if (response?.status !== 200) throw new Error(response?.data?.error);
    setDistrictStockist(
      response?.data?.data?.map((item: any) => ({
        ...item,
        cluster_name_company_stockist_code: `${item?.cluster_name} - ${item?.owner_name}`,
        unique_stockist_id: `${item?.cluster_code}-${item?.stockist_id}`,
      }))
    );
  };

  console.log({ clusterCode });

  useEffect(() => {
    fetchProductsData();
  }, []);

  useEffect(() => {
    if (clusterCode) {
      fetchDistrictStockistData();
    }
  }, [clusterCode]);

  return (
    <div className="flex flex-col gap-y-2 mb-4 w-[340px] max-h-[318px] mx-auto px-6 bg-white p-4 rounded-xl">
      <div className="flex justify-between items-center">
        <p className="text-normal py-1 font-semibold text-[#49484C]">Change Stockist</p>
        <Button className="bg-transparent -mr-3" onClick={onCloseModal}>
          <img src={crossIcon} alt="cross" className="w-5 h-5 font-bold invert cursor-pointer" />
        </Button>
      </div>
      <div className="">
        <p className="text-sm mb-2 mt-2 font-normal text-[#666666]">Stockist Code</p>
        {/* <p className="text-sm text-[#616161]">Invoice No</p> */}
        {/* <Input
          type="text"
          placeholder="Enter Stockist Code"
          value={stockistCode}
          onChange={(e) => setStockistCode(e.target.value)}
          className="w-full border py-4 border-[#D0D0D0] rounded-md p-2 h-8"
        /> */}
        <Autocomplete
          placeholder="Select Stockist"
          suggestionList={districtStockist}
          displayKey="cluster_name_company_stockist_code"
          uniqueKey="unique_stockist_id"
          onSelectionChange={(e: any) => setSelectedStockist(e)}
        />
      </div>
      <div>
        <p className="text-sm mb-2 mt-2 font-normal text-[#666666]">Remarks</p>
        {/* <p className="text-sm text-[#616161]">Invoice No</p> */}
        <Input
          type="text"
          placeholder="Enter Remarks"
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          className="w-full border py-4 border-[#D0D0D0] rounded-md p-2 h-8"
        />
      </div>
      <p className="text-sm mb-2 mt-2 font-normal text-[#666666]">
        Make Default Stockist{" "}
        <span
          className={`text-[#687182] cursor-pointer text-[10px] bg-[#E9EDF5] px-2 py-1 rounded-full ${makeDefaultStockist ? "bg-[#8051FF]" : ""}`}
          onClick={() => setMakeDefaultStockist(!makeDefaultStockist)}
        >
          {makeDefaultStockist ? <span className="text-[#8051FF]">On</span> : <span className="text-[#687182]">Off</span>}
        </span>
      </p>

      <Button isLoading={isLoading} loaderSize="small" className="text-white bg-[#8051FF] mt-2" onClick={handleSaveChanges}>
        Save Changes
      </Button>
    </div>
  );
};

export default EditStockistName;
